import { Camera, RotateCcw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";
import { Button } from "./Button";

type AdminStlSnapshotViewerProps = {
  file?: File | null;
  url?: string | null;
  sourceName?: string;
  onSnapshotReady: (snapshotFile: File, previewUrl: string) => void;
};

export function AdminStlSnapshotViewer({ file, url, sourceName, onSnapshotReady }: AdminStlSnapshotViewerProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const meshRef = useRef<THREE.Mesh | null>(null);
  const previewUrlRef = useRef<string>("");
  const [status, setStatus] = useState<"empty" | "loading" | "ready" | "error">("empty");
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, (host.clientWidth || 640) / (host.clientHeight || 360), 0.1, 2000);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(host.clientWidth || 640, host.clientHeight || 360);
    renderer.setClearColor(0x000000, 0);
    host.innerHTML = "";
    host.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.enablePan = false;
    controls.touches.ONE = THREE.TOUCH.ROTATE;
    controls.touches.TWO = THREE.TOUCH.DOLLY_PAN;

    scene.add(new THREE.AmbientLight(0xffffff, 0.65));
    const mainLight = new THREE.DirectionalLight(0xffffff, 0.9);
    mainLight.position.set(1, 1, 1);
    scene.add(mainLight);
    const fillLight = new THREE.DirectionalLight(0x7db4ff, 0.3);
    fillLight.position.set(-1, -0.5, -1);
    scene.add(fillLight);

    rendererRef.current = renderer;
    cameraRef.current = camera;
    controlsRef.current = controls;
    sceneRef.current = scene;

    const resize = () => {
      const nextHost = hostRef.current;
      const nextRenderer = rendererRef.current;
      const nextCamera = cameraRef.current;
      if (!nextHost || !nextRenderer || !nextCamera) return;
      const width = nextHost.clientWidth || 640;
      const height = nextHost.clientHeight || 360;
      nextCamera.aspect = width / height;
      nextCamera.updateProjectionMatrix();
      nextRenderer.setSize(width, height);
    };

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(host);

    let frame = 0;
    const animate = () => {
      frame = window.requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      window.cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      controls.dispose();
      meshRef.current?.geometry.dispose();
      if (Array.isArray(meshRef.current?.material)) meshRef.current.material.forEach((item) => item.dispose());
      else meshRef.current?.material.dispose();
      renderer.dispose();
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
  }, []);

  useEffect(() => {
    if (!file && !url) {
      setStatus("empty");
      setPreviewUrl("");
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = "";
      }
      const scene = sceneRef.current;
      const mesh = meshRef.current;
      if (scene && mesh) {
        scene.remove(mesh);
        mesh.geometry.dispose();
        if (Array.isArray(mesh.material)) mesh.material.forEach((item) => item.dispose());
        else mesh.material.dispose();
        meshRef.current = null;
      }
      return;
    }

    const scene = sceneRef.current;
    const camera = cameraRef.current;
    const controls = controlsRef.current;
    if (!scene || !camera || !controls) return;

    setStatus("loading");
    setPreviewUrl("");
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = "";
    }

    const loader = new STLLoader();
    const loadGeometry = (geometry: THREE.BufferGeometry) => {
      try {
        geometry.computeBoundingBox();
        geometry.center();

        if (meshRef.current) {
          scene.remove(meshRef.current);
          meshRef.current.geometry.dispose();
          if (Array.isArray(meshRef.current.material)) meshRef.current.material.forEach((item) => item.dispose());
          else meshRef.current.material.dispose();
          meshRef.current = null;
        }

        const material = new THREE.MeshPhongMaterial({
          color: 0x4d93f8,
          specular: 0x222222,
          shininess: 50,
          flatShading: true
        });

        const mesh = new THREE.Mesh(geometry, material);
        const size = new THREE.Vector3();
        geometry.boundingBox?.getSize(size);
        const maxDim = Math.max(size.x, size.y, size.z) || 1;
        const scale = 100 / maxDim;
        mesh.scale.set(scale, scale, scale);
        mesh.rotation.x = -Math.PI / 2;
        scene.add(mesh);
        meshRef.current = mesh;

        resetView();
        setStatus("ready");
      } catch {
        setStatus("error");
      }
    };

    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const geometry = loader.parse(event.target?.result as ArrayBuffer);
          loadGeometry(geometry);
        } catch {
          setStatus("error");
        }
      };
      reader.onerror = () => setStatus("error");
      reader.readAsArrayBuffer(file);
      return;
    }

    loader.load(
      url!,
      (geometry) => loadGeometry(geometry),
      undefined,
      () => setStatus("error")
    );
  }, [file, url]);

  function resetView() {
    const camera = cameraRef.current;
    const controls = controlsRef.current;
    if (!camera || !controls) return;
    camera.position.set(120, 80, 120);
    camera.lookAt(0, 0, 0);
    controls.target.set(0, 0, 0);
    controls.update();
  }

  function captureSnapshot() {
    const renderer = rendererRef.current;
    if (!renderer || status !== "ready") return;
    renderer.render(sceneRef.current!, cameraRef.current!);
    renderer.domElement.toBlob((blob) => {
      if (!blob) return;
      const rawName = sourceName || file?.name || url?.split("/").pop() || "product.stl";
      const base = rawName.replace(/\.stl$/i, "") || "product";
      const snapshotFile = new File([blob], `${base}.png`, { type: "image/png" });
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      const nextUrl = URL.createObjectURL(blob);
      previewUrlRef.current = nextUrl;
      setPreviewUrl(nextUrl);
      onSnapshotReady(snapshotFile, nextUrl);
    }, "image/png");
  }

  return (
    <div className="admin-stl-snapshot">
      <div ref={hostRef} className="admin-stl-snapshot-canvas" />
      <div className="admin-stl-snapshot-overlay">
        {status === "empty" ? <span>Upload an STL to position it for a product snapshot.</span> : null}
        {status === "loading" ? <span>Loading STL preview...</span> : null}
        {status === "error" ? <span>Could not load STL preview.</span> : null}
      </div>
      <div className="admin-stl-snapshot-actions">
        <Button type="button" variant="ghost" size="sm" icon={<RotateCcw size={15} />} onClick={resetView} disabled={status !== "ready"}>
          Reset View
        </Button>
        <Button type="button" size="sm" icon={<Camera size={15} />} onClick={captureSnapshot} disabled={status !== "ready"}>
          Take Picture
        </Button>
      </div>
      {previewUrl ? (
        <div className="admin-stl-snapshot-preview">
          <span className="text-upper text-accent">Snapshot Preview</span>
          <img src={previewUrl} alt="STL snapshot preview" />
        </div>
      ) : null}
    </div>
  );
}
